import { AxiosError } from 'axios';
import { LinkCardTabs } from 'components/Card/CardTabs/LinkCardTabs';
import { Card } from 'components/common';
import { CommonButton } from 'components/common/Forms/Button';
import { Col, Grid } from 'components/common/Grid';
import { PageHelmet } from 'components/common/PageHelmet';
import { ConfirmDialog } from 'components/Popup/ConfirmDialog/ConfirmDialog';
import { TableActions } from 'components/Table/Table.styled';
import { ApptOptionsForm } from 'components/views-components/staff/settings/apptOptions/ApptOptionsForm';
import { ApptOptionsTable } from 'components/views-components/staff/settings/apptOptions/ApptOptionsTable';
import { useSite } from 'context/SiteContext';
import { useEffect, useState } from 'react';
import { FaTrashAlt } from 'react-icons/fa';
import {
  ApptOption,
  ApptOptionsFilters,
  ApptOptionsFormValues,
} from 'types/apptOptions';
import { errorToast, successToast } from 'utils/toast';
import { StaffLayout } from 'containers/staff/StaffLayout';
import { usePaginatedFilters } from 'hooks/usePaginatedFilters';
import {
  useApptOptions,
  useMutateCreateApptOption,
  useMutateDeleteApptOption,
  useMutateUpdateApptOption,
} from 'query/apptOptions';
import { useCommonTranslation } from 'hooks/i18n/useCommonTranslation';
import { useStaffListsTabs } from 'hooks/lists/useStaffListsTabs';

export const ApptOptions = () => {
  const { t } = useCommonTranslation();
  const listsTabs = useStaffListsTabs();
  const { activeSite } = useSite();
  const { filters, setFilters } = usePaginatedFilters<ApptOptionsFilters>();
  const {
    isLoading: isListLoading,
    isFetching: isListFetching,
    data,
  } = useApptOptions(filters);
  const { isLoading: isUpdateLoading, mutate: update } =
    useMutateUpdateApptOption({
      onSuccess: () => {
        successToast(t('changes-have-been-saved'));
        closeFormHandler();
      },
    });
  const { isLoading: isCreateLoading, mutate: create } =
    useMutateCreateApptOption({
      onSuccess: () => {
        successToast(t('appointment-option-added'));
        closeFormHandler();
      },
    });
  const { isLoading: isDeleteLoading, mutate: deleteItem } =
    useMutateDeleteApptOption({
      onSuccess: () => {
        successToast(t('appointment-option-deleted'));
        closeFormHandler();
      },
    });

  const isLoading =
    isCreateLoading || isUpdateLoading || isListLoading || isDeleteLoading;

  const [showForm, setShowForm] = useState(false);
  const [prefilledData, setPrefilledData] = useState<ApptOption>();
  const [confirmDeleteItem, setConfirmDeleteItem] = useState<ApptOption>();

  const submitHandler = (formValues: ApptOptionsFormValues) => {
    prefilledData
      ? update({ ...prefilledData, ...formValues })
      : create(formValues);
  };

  const closeFormHandler = (err?: AxiosError | string) => {
    setShowForm(false);
    setPrefilledData(undefined);
    setConfirmDeleteItem(undefined);
    if (err) {
      errorToast(err);
    }
  };

  const openFormHandler = (value?: string) => {
    if (!value) {
      setPrefilledData(undefined);
      setShowForm(true);
      return;
    }

    const selectedApptOption = data?.data?.find(
      (apptOption) => apptOption.id === value,
    );

    if (selectedApptOption) {
      setPrefilledData(selectedApptOption);
      setShowForm(true);
    } else {
      closeFormHandler(t('appointment-option-not-found'));
    }
  };

  const handleDeleteApptOption = (value: string) => {
    setConfirmDeleteItem(
      data?.data?.find((apptOption) => apptOption.id === value),
    );
  };

  const handleConfirmDeleteApptOption = () => {
    deleteItem(confirmDeleteItem?.id as string);
  };

  useEffect(() => {
    closeFormHandler();
  }, [activeSite?.id, filters]);

  return (
    <StaffLayout>
      <PageHelmet title={t('appointment-options')} />
      <Grid>
        <Col md={8}>
          <Card fillHeight flex>
            {listsTabs.length > 1 && (
              <LinkCardTabs withDivider tabs={listsTabs} />
            )}
            <TableActions>
              <CommonButton variant="primary" onClick={() => openFormHandler()}>
                {t('add-appointment-option')}
              </CommonButton>
            </TableActions>
            <ApptOptionsTable
              apptOptionsData={data}
              openFormHandler={openFormHandler}
              handleDeleteApptOption={handleDeleteApptOption}
              setFilters={setFilters}
              isLoading={isListLoading}
              isFetching={isListFetching}
              filters={filters}
            />
          </Card>
        </Col>
        <Col md={4}>
          {showForm && (
            <ApptOptionsForm
              key={prefilledData?.id ?? 'new'}
              closeFormHandler={closeFormHandler}
              submitHandler={submitHandler}
              isLoading={isLoading}
              prefilledData={prefilledData}
              handleDeleteApptOption={handleDeleteApptOption}
            />
          )}
        </Col>
      </Grid>
      {Boolean(confirmDeleteItem) && (
        <ConfirmDialog
          isLoading={isLoading}
          handleClose={() => closeFormHandler()}
          icon={<FaTrashAlt />}
          title={t('confirm-delete-appointment-option')}
          status="delete"
          actions={[
            <CommonButton
              variant="secondary"
              onClick={() => {
                setConfirmDeleteItem(undefined);
              }}
              key={confirmDeleteItem?.id + '-cancel'}
            >
              {t('cancel')}
            </CommonButton>,
            <CommonButton
              variant="danger"
              onClick={handleConfirmDeleteApptOption}
              key={confirmDeleteItem?.id + '-delete'}
            >
              {t('delete')}
            </CommonButton>,
          ]}
        />
      )}
    </StaffLayout>
  );
};
